import React, {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import AccelerationRatio from '@di-shared/features/home/kr/ev-battery/participatory-care/AccelerationRatio';
import HighSpeedRatio from '@di-shared/features/home/kr/ev-battery/participatory-care/HighSpeedRatio';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface ParticipatoryEvBatteryCareDrivingProps {
  vin: string;
  puid: string;
}

function ParticipatoryEvBatteryCareDriving(props: ParticipatoryEvBatteryCareDrivingProps) {
  return (
    <ErrorBoundary fallback={<NoData />}>
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

export default ParticipatoryEvBatteryCareDriving;

function Inner({vin, puid}: ParticipatoryEvBatteryCareDrivingProps) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {
    data: {data},
  } = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.evBatteryManagementController.getEvBatteryManagementTrip), vin, puid],
    queryFn: () => diApi.evBatteryManagementController.getEvBatteryManagementTrip({vin, puid}),
  });
  if (!data) {
    return <NoData />;
  }
  const updateDate = parseDateWithFormat(data.lastUpdatedDate);
  const drivingCount = data.tripCount;
  return (
    <>
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30401_count', {count: drivingCount})}
        </h4>
      </div>
      {updateDate && (
        <p className="section_container_desc">
          {t('T30001')} : {CldrDatetimeFormatter.format(updateDate, 'yMMMd')}
        </p>
      )}
      <div className="section_container_box">
        <HighSpeedRatio
          user={{highSpeedDrivingCount: data.highSpeedCount, normalSpeedDrivingCount: data.normalSpeedCount}}
          avgCustomer={{
            highSpeedDrivingCount: data.avgHighSpeedCount,
            normalSpeedDrivingCount: data.avgNormalSpeedCount,
          }}
          highSpeedRateStatus={data.highSpeedRateStatus}
        />
        <AccelerationRatio
          user={{accelerationDrivingCount: data.harshDrivingCount, normalDrivingCount: data.normalDrivingCount}}
          avgCustomer={{
            accelerationDrivingCount: data.avgHarshDrivingCount,
            normalDrivingCount: data.avgNormalDrivingCount,
          }}
          harshDrivingRateStatus={data.harshDrivingRateStatus}
        />
      </div>
    </>
  );
}

function NoData() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="empty_area">
        <span className="ic ic_car_24px" />
        <strong className="empty_title">{t('T30405')}</strong>
        <span
          className="body_small_r"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30407')}
        </span>
      </div>
      <div className="section_container_box">
        <ul className="notice_list">
          <li className="notice_item">{t('T30644')}</li>
          <li className="notice_item">{t('T30645')}</li>
          <li className="notice_item">{t('T30646')}</li>
        </ul>
      </div>
    </>
  );
}

function Loading() {
  const {t} = useInferableTranslation();
  return (
    <>
      <div className="section_container_header">
        <h4
          className="title_xsmall_b"
          style={{whiteSpace: 'pre-wrap'}}
        >
          {t('T30401_count', {count: '-'})}
        </h4>
      </div>
      <p className="section_container_desc">{t('T30001')} : -</p>
      <div
        className="section_container_box skeleton"
        style={{height: 409}}
      />
    </>
  );
}
